import { Card } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { type ShiftPeriod, shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { parseISO } from "date-fns";

import { type ShiftType } from "../api/usePaginatedWorkerShifts";
import { ShiftHistoryCardContent } from "./CardContent";

interface ShiftHistoryCardProps {
  shift: ShiftType;
  workplaceInfo?: {
    name?: string;
    timezone?: string;
  };
  totalShiftTimeInHours: number;
  isMissedPunchRequestEnabled: boolean;
  hasPendingExtraWorkedTimeRequest: boolean;
  isTimesheetUploaded: boolean;
  onUploadTimesheetClick: () => void;
  onChatWithSupportClick: () => void;
}

export function ShiftHistoryCard(props: ShiftHistoryCardProps) {
  const {
    shift,
    workplaceInfo,
    totalShiftTimeInHours,
    isMissedPunchRequestEnabled,
    hasPendingExtraWorkedTimeRequest,
    isTimesheetUploaded,
    onUploadTimesheetClick,
    onChatWithSupportClick,
  } = props;
  const qualificationName = shift.attributes?.requirements?.qualificationName ?? "";
  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([
    qualificationName,
  ]);

  const qualificationText =
    qualificationNameToDisplayLabelMap.get(qualificationName) ?? qualificationName;

  if (!isDefined(shift) || !isDefined(shift.relationships?.workplace?.data?.id)) {
    return null;
  }

  const shiftClockInTime =
    shift.attributes?.clockInOut?.start ?? shift.attributes?.schedule?.startAt;
  const shiftClockOutTime = shift.attributes?.clockInOut?.end ?? shift.attributes?.schedule?.endAt;

  if (!isDefined(shiftClockInTime) || !isDefined(shiftClockOutTime)) {
    return null;
  }

  const workplaceId = shift.relationships.workplace.data.id;

  if (!isDefined(workplaceInfo?.name)) {
    return null;
  }

  // pay.total is the hourly pay rate for the shift
  // totalShiftTimeInHours includes shift time and approved break time
  const totalPayInDollars = totalShiftTimeInHours * (shift.attributes?.pay?.total ?? 0);
  const timeSlot = shift.attributes?.schedule?.timeSlot;

  return (
    <Card
      key={shift.id}
      outlined
      sx={{
        flexGrow: 1,
        marginBottom: 6,
      }}
      variant="quaternary"
    >
      <ShiftHistoryCardContent
        shiftId={shift.id}
        timeSlot={shiftPeriodToTimeSlot[timeSlot as ShiftPeriod]}
        workplaceId={workplaceId}
        workplaceName={workplaceInfo?.name ?? ""}
        workplaceTimezone={workplaceInfo?.timezone ?? DEFAULT_TIMEZONE}
        shiftStartDate={parseISO(shiftClockInTime)}
        shiftEndDate={parseISO(shiftClockOutTime)}
        totalPayInDollars={totalPayInDollars}
        qualificationText={qualificationText}
        isVerified={shift.attributes?.verified ?? false}
        isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
        hasPendingExtraWorkedTimeRequest={hasPendingExtraWorkedTimeRequest}
        isTimesheetUploaded={isTimesheetUploaded}
        onUploadTimesheetClick={onUploadTimesheetClick}
        onChatWithSupportClick={onChatWithSupportClick}
      />
    </Card>
  );
}
